import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Radio } from 'antd';

import ModalWrapper from './ModalWrapper';

const UNLINK_REASONS = [
  { value: 'wrong_patient', label: 'Wrong patient' },
  { value: 'duplicate', label: 'Duplicate document' },
  { value: 'linked_in_error', label: 'Linked in error' },
  { value: 'other', label: 'Other' },
];

const radioStyle = {
  display: 'block',
  height: '30px',
  lineHeight: '30px',
};

const UnlinkReferralModal = ({
  hideModal,
  isSubmitting,
  onConfirm,
  recordPatientName,
}) => {
  const [reason, setReason] = useState(null);
  const [reasonDetail, setReasonDetail] = useState('');

  const detailRequired = reason === 'other';
  const detailIsBlank = reasonDetail.trim().length === 0;
  const submitDisabled = useMemo(
    () => !reason || (detailRequired && detailIsBlank),
    [reason, detailRequired, detailIsBlank],
  );

  const stopModalEvent = (event) => {
    event?.stopPropagation();
  };

  const handleHideModal = (event) => {
    stopModalEvent(event);
    hideModal();
  };

  const handleConfirm = (event) => {
    stopModalEvent(event);
    if (submitDisabled) return;

    const payload = { reason };
    if (detailRequired) {
      payload.reason_detail = reasonDetail.trim();
    }

    onConfirm(payload);
  };

  const subtitle = recordPatientName
    ? `Why are you unlinking this record from ${recordPatientName}'s referral?`
    : 'Why are you unlinking this record?';

  return (
    <ModalWrapper
      action={handleConfirm}
      actionButtonDanger
      actionName="Unlink"
      customContentStyle={{ top: 80 }}
      disabled={submitDisabled}
      dismiss="Cancel"
      hideModal={handleHideModal}
      isLoading={isSubmitting}
      modalTitle="Unlink from referral?"
      width={520}
    >
      <div onClick={stopModalEvent} onMouseDown={stopModalEvent} role="presentation">
        <p style={{ marginBottom: 12 }}>{subtitle}</p>
        <p style={{ marginBottom: 12, color: '#6B7280' }}>
          The unlinked record will be returned to the primary record&apos;s current status.
          If the primary has no status, the record&apos;s status will be cleared.
        </p>
        <Radio.Group
          aria-label="Unlink reason"
          onChange={(event) => setReason(event.target.value)}
          value={reason}
        >
          {UNLINK_REASONS.map(({ label, value }) => (
            <Radio key={value} style={radioStyle} value={value}>
              {label}
            </Radio>
          ))}
        </Radio.Group>
        {detailRequired && (
          <div style={{ marginTop: 12 }}>
            <div
              id="unlink-reason-detail-label"
              style={{ display: 'block', fontWeight: 600, marginBottom: 4 }}
            >
              Please provide additional detail
            </div>
            <Input.TextArea
              aria-labelledby="unlink-reason-detail-label"
              autoFocus
              id="unlink-reason-detail"
              maxLength={500}
              onChange={(event) => setReasonDetail(event.target.value)}
              placeholder="Describe why this record is being unlinked"
              rows={3}
              value={reasonDetail}
            />
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

UnlinkReferralModal.defaultProps = {
  isSubmitting: false,
  recordPatientName: null,
};

UnlinkReferralModal.propTypes = {
  hideModal: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  recordPatientName: PropTypes.string,
};

export default UnlinkReferralModal;
